/* eslint-disable react-hooks/set-state-in-effect */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Calendar } from '@uhg-abyss/web/ui/Calendar';
import { useToken } from '@uhg-abyss/web/hooks/useToken';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Text } from '@uhg-abyss/web/ui/Text';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import type { CalendarModalProps, ParsedSlot } from './CalendarModal.types';
import { parseDateTime } from '../../../../optumus-core/src/utilities/dataParse.utils';

/* ---------- CONSTANTS ---------- */
const { innerWidth: SCREEN_WIDTH, innerHeight: SCREEN_HEIGHT } = window;
const SHEET_PADDING = Math.round(SCREEN_WIDTH * 0.048); // ~18px on 375
const SHEET_MARGIN_H = Math.round(SCREEN_WIDTH * 0.04); // ~15px on 375
const SHEET_BORDER_R = Math.round(SCREEN_WIDTH * 0.064); // ~24px on 375
const SHEET_SHADOW_R = Math.round(SCREEN_WIDTH * 0.133); // ~50px on 375
const SHEET_SHADOW_Y = Math.round(SCREEN_HEIGHT * 0.022); // ~18px on 812
const BTN_BORDER_R = Math.round(SCREEN_WIDTH * 0.032); // ~12px on 375
const SLOT_PAD_H = Math.round(SCREEN_WIDTH * 0.048); // ~18px on 375
const CLOSE_PAD = Math.round(SCREEN_WIDTH * 0.016); // ~6px  on 375
const LABEL_LETTER_SP = SCREEN_WIDTH * 0.0016; // ~0.6  on 375
const TITLE_LETTER_SP = -(SCREEN_WIDTH * 0.0016); // ~-0.6 on 375
const SECTION_LABEL_MB = Math.round(SCREEN_WIDTH * 0.021); // ~8px  on 375
const SECTION_LABEL_MT = Math.round(SCREEN_WIDTH * 0.011); // ~4px  on 375
const SLOT_GRID_GAP = Math.round(SCREEN_WIDTH * 0.021); // ~8px  on 375
const ACTIONS_GAP = Math.round(SCREEN_WIDTH * 0.032); // ~12px on 375
const CANCEL_BORDER_W = 1.5;
const SLOT_BTN_H = 40; // min touch target ~40px
const SAVE_BTN_H = 48; // min touch target ~48px

/**
 * Builds a "YYYY-MM-DD" string key from year, month (0-indexed), and day.
 * Used as the lookup key for grouping and retrieving availability slots by calendar date.
 */
const toDateKey = (year: number, month: number, day: number): string =>
  `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

const getInitialSelectedDate = (availableDates: Set<string>, todayStart: Date): Date => {
  const todayKey = toDateKey(todayStart.getFullYear(), todayStart.getMonth(), todayStart.getDate());

  if (availableDates.has(todayKey)) {
    return todayStart;
  }

  const earliestKey = [...availableDates].sort()[0];
  if (!earliestKey) {
    return todayStart;
  }

  const [year, month, day] = earliestKey.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/* ---------- STYLED COMPONENTS ---------- */

// — Modal overlay (dimmed backdrop)
const ModalOverlay = styled('div', {
  flex: 1,
  justifyContent: 'flex-end',
  backgroundColor: 'rgba(0, 0, 0, 0.25)', // 25% opacity black,
});

// — Bottom sheet card
const ModalSheet = styled('div', {
  borderRadius: SHEET_BORDER_R,
  padding: SHEET_PADDING,
  maxHeight: SCREEN_HEIGHT * 0.9,
  shadowOffset: { width: 0, height: -SHEET_SHADOW_Y },
  shadowOpacity: 0.22,
  shadowRadius: SHEET_SHADOW_R,
  elevation: 20,
  marginHorizontal: SHEET_MARGIN_H,
  marginBottom: SHEET_MARGIN_H,
  props: ({ bg, shadowColor }: { bg?: string; shadowColor?: string }) => ({
    backgroundColor: bg,
    shadowColor,
  }),
});

// — Modal header row (title + close button)
const ModalHeader = styled('div', {
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '$core.spacing.200',
});

const ModalTitleText = styled(Text, {
  letterSpacing: TITLE_LETTER_SP,
  fontSize: '$core.font-size.h.40',
  fontWeight: '700',
  props: ({ color }: { color?: string }) => ({ color }),
});

const ModalCloseButton = styled('button', {
  padding: CLOSE_PAD,
  borderRadius: '$core.border-radius.full',
  borderColor: '#dfe5f1',
  borderWidth: 1,
  width: '$core.sizing.400',
  height: '$core.sizing.400',
  justifyContent: 'center',
  alignItems: 'center',
});

const CalendarContainer = styled('div', {
  marginBottom: '$core.spacing.50',
});

// — Time slot section label (Morning / Afternoon)
const SlotSectionLabel = styled(Text, {
  letterSpacing: LABEL_LETTER_SP,
  marginBottom: SECTION_LABEL_MB,
  marginTop: SECTION_LABEL_MT,
  fontSize: '$core.font-size.p.40',
  fontWeight: '900',
  props: ({ color }: { color?: string }) => ({ color }),
});

// — Time slot grid and buttons
const TimeSlotGrid = styled('div', {
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: SLOT_GRID_GAP,
  marginBottom: '$core.spacing.50',
});

const TimeSlotButton = styled('button', {
  minHeight: SLOT_BTN_H,
  paddingVertical: '$core.spacing.100',
  paddingHorizontal: SLOT_PAD_H,
  borderRadius: 999,
  borderWidth: '$core.border-width.sm',
  marginRight: '$core.spacing.100',
  marginBottom: '$core.spacing.100',
  justifyContent: 'center',
  alignItems: 'center',
  props: ({ bg, borderColor }: { bg?: string; borderColor?: string }) => ({
    backgroundColor: bg,
    borderColor,
  }),
});

// — Confirm / cancel action buttons
const ModalActionRow = styled('div', {
  flexDirection: 'row',
  gap: ACTIONS_GAP,
  marginTop: '$core.spacing.200',
});

const CancelButton = styled('button', {
  flex: 1,
  paddingVertical: '$core.spacing.150',
  paddingHorizontal: '$core.spacing.200',
  borderRadius: BTN_BORDER_R,
  borderWidth: CANCEL_BORDER_W,
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: SAVE_BTN_H,
  props: ({ borderColor }: { borderColor?: string }) => ({ borderColor }),
});

const SaveButton = styled('button', {
  flex: 1,
  paddingVertical: '$core.spacing.150',
  paddingHorizontal: '$core.spacing.200',
  borderRadius: BTN_BORDER_R,
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: SAVE_BTN_H,
  props: ({ bg }: { bg?: string }) => ({ backgroundColor: bg }),
});

const SaveButtonContent = styled('div', {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

// — Text nodes
const CloseButtonIcon = styled(Text, {
  fontSize: '$core.font-size.p.100',
  fontWeight: '700',
  props: ({ color }: { color?: string }) => ({ color }),
});

const TimeSlotLabel = styled(Text, {
  fontSize: '$core.font-size.p.40',
  fontWeight: '800',
  props: ({ color }: { color?: string }) => ({ color }),
});

const CancelButtonLabel = styled(Text, {
  fontSize: '$core.font-size.p.80',
  fontWeight: '700',
  props: ({ color }: { color?: string }) => ({ color }),
});

const SaveButtonLabel = styled(Text, {
  fontSize: '$core.font-size.p.80',
  fontWeight: '700',
  props: ({ color }: { color?: string }) => ({ color }),
});

const SaveButtonIcon = styled(Text, {
  fontSize: '$core.font-size.p.80',
  fontWeight: '700',
  props: ({ color }: { color?: string }) => ({ color }),
});
/* ---------- COMPONENT ---------- */
export const CalendarModal: React.FC<CalendarModalProps> = ({
  isVisible,
  onClose,
  availabilitySlots,
  onSlotConfirm,
}) => {
  const getColor = useToken('colors');
  const todayStart = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const accent = getColor('$core.color.brand.80');
  const sheetBg = getColor('$core.color.neutral.0');
  const slotBg = getColor('$core.color.blue.10');
  const textPrimary = getColor('$core.color.neutral.90');
  const textSecondary = getColor('$core.color.neutral.60');
  const slateNavy = getColor('$core.color.neutral.90');
  const textOnAccent = getColor('$core.color.neutral.0');
  const textDeactivated = getColor('$core.color.neutral.50');
  const timeSlotBorder = getColor('$core.color.neutral.20');
  const cancelButtonBorder = getColor('$core.color.neutral.20');
  const optumBlue = getColor('$core.color.brand.80');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<ParsedSlot | null>(null);

  /**
   * Groups all availability slots by calendar date.
   * Parses each slot's dateTime string, builds a "YYYY-MM-DD" key,
   * and stores the parsed time (12h format) and hour for time-of-day grouping.
   * Re-computes only when availabilitySlots changes.
   */
  const slotsByDate = useMemo(() => {
    const map = new Map<string, ParsedSlot[]>();
    for (const slot of availabilitySlots) {
      const parsed = parseDateTime(slot.dateTime);
      const key = toDateKey(
        parsed.date.getFullYear(),
        parsed.date.getMonth(),
        parsed.date.getDate(),
      );

      const existing = map.get(key) ?? [];
      existing.push({ slot, time12h: parsed.time12h, hour: parsed.hour });
      map.set(key, existing);
    }
    return map;
  }, [availabilitySlots]);

  // Set of date keys that have availability
  const availableDates = useMemo(() => new Set(slotsByDate.keys()), [slotsByDate]);

  // Reset selection when modal opens: prefer today if available, else first available slot date.
  useEffect(() => {
    if (isVisible) {
      setSelectedDate(getInitialSelectedDate(availableDates, todayStart));
      setSelectedSlot(null);
    }
  }, [isVisible, availableDates, todayStart]);

  const selectableDateRange = useMemo(() => {
    let minDate: Date | null = null;
    let maxDate: Date | null = null;

    for (const key of slotsByDate.keys()) {
      const [year, month, day] = key.split('-').map(Number);
      const slotDate = new Date(year, month - 1, day);

      if (!minDate || slotDate.getTime() < minDate.getTime()) {
        minDate = slotDate;
      }
      if (!maxDate || slotDate.getTime() > maxDate.getTime()) {
        maxDate = slotDate;
      }
    }

    return { min: minDate ?? todayStart, max: maxDate ?? todayStart };
  }, [slotsByDate, todayStart]);

  const excludeDate = useCallback(
    (value: Dayjs): boolean => {
      const dateValue = value.toDate();
      const valueStart = new Date(
        dateValue.getFullYear(),
        dateValue.getMonth(),
        dateValue.getDate(),
      );

      const key = toDateKey(valueStart.getFullYear(), valueStart.getMonth(), valueStart.getDate());

      return valueStart.getTime() < todayStart.getTime() || !availableDates.has(key);
    },
    [availableDates, todayStart],
  );

  // Get grouped time slots for currently selected day
  const daySlotsGrouped = useMemo(() => {
    if (!selectedDate) return { morning: [], afternoon: [], evening: [] };

    const key = toDateKey(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
    );

    const slots = slotsByDate.get(key) ?? [];

    return {
      morning: slots.filter((s) => s.hour < 12),
      afternoon: slots.filter((s) => s.hour >= 12 && s.hour < 17),
      evening: slots.filter((s) => s.hour >= 17),
    };
  }, [selectedDate, slotsByDate]);

  const handleDateChange = (value: Dayjs) => {
    setSelectedDate(value.toDate());
    setSelectedSlot(null);
  };

  const handleSave = () => {
    if (selectedSlot) {
      onSlotConfirm?.(selectedSlot.slot);
    }
    onClose();
  };

  return (
    <ModalDialog
      isOpen={isVisible}
      // transparent
      // animationType="slide"
      onClose={onClose}
    >
      <ModalOverlay>
        <ModalSheet bg={sheetBg} shadowColor={textPrimary}>
          {/* Modal header */}
          <ModalHeader>
            <ModalTitleText color={optumBlue}>Select date and time</ModalTitleText>
            <ModalCloseButton onPress={onClose}>
              <CloseButtonIcon color={slateNavy}>✕</CloseButtonIcon>
            </ModalCloseButton>
          </ModalHeader>

          <div style={{ overflowY: 'auto', height: '100vh' }}>
            <CalendarContainer>
              <Calendar
                selectedDates={selectedDate ? dayjs(selectedDate) : undefined}
                onDateClick={handleDateChange}
                minDate={dayjs(selectableDateRange.min)}
                maxDate={dayjs(selectableDateRange.max)}
                excludeDate={excludeDate}
              />
            </CalendarContainer>

            {/* Time slots grouped by period */}
            {daySlotsGrouped.morning.length === 0 &&
              daySlotsGrouped.afternoon.length === 0 &&
              daySlotsGrouped.evening.length === 0 && (
                <SlotSectionLabel color={textSecondary}>
                  No available times for this date
                </SlotSectionLabel>
              )}

            {daySlotsGrouped.morning.length > 0 && (
              <>
                <SlotSectionLabel color={textPrimary}>Morning</SlotSectionLabel>
                <TimeSlotGrid>
                  {daySlotsGrouped.morning.map((parsed) => (
                    <TimeSlotButton
                      key={parsed.slot.appointmentId}
                      bg={
                        selectedSlot?.slot.appointmentId === parsed.slot.appointmentId
                          ? optumBlue
                          : slotBg
                      }
                      borderColor={
                        selectedSlot?.slot.appointmentId === parsed.slot.appointmentId
                          ? accent
                          : timeSlotBorder
                      }
                      onPress={() => setSelectedSlot(parsed)}
                    >
                      <TimeSlotLabel
                        color={
                          selectedSlot?.slot.appointmentId === parsed.slot.appointmentId
                            ? textOnAccent
                            : optumBlue
                        }
                      >
                        {parsed.time12h}
                      </TimeSlotLabel>
                    </TimeSlotButton>
                  ))}
                </TimeSlotGrid>
              </>
            )}

            {daySlotsGrouped.afternoon.length > 0 && (
              <>
                <SlotSectionLabel color={textPrimary}>Afternoon</SlotSectionLabel>
                <TimeSlotGrid>
                  {daySlotsGrouped.afternoon.map((parsed) => (
                    <TimeSlotButton
                      key={parsed.slot.appointmentId}
                      bg={
                        selectedSlot?.slot.appointmentId === parsed.slot.appointmentId
                          ? optumBlue
                          : slotBg
                      }
                      borderColor={
                        selectedSlot?.slot.appointmentId === parsed.slot.appointmentId
                          ? accent
                          : timeSlotBorder
                      }
                      onPress={() => setSelectedSlot(parsed)}
                    >
                      <TimeSlotLabel
                        color={
                          selectedSlot?.slot.appointmentId === parsed.slot.appointmentId
                            ? textOnAccent
                            : optumBlue
                        }
                      >
                        {parsed.time12h}
                      </TimeSlotLabel>
                    </TimeSlotButton>
                  ))}
                </TimeSlotGrid>
              </>
            )}

            {daySlotsGrouped.evening.length > 0 && (
              <>
                <SlotSectionLabel color={textPrimary}>Evening</SlotSectionLabel>
                <TimeSlotGrid>
                  {daySlotsGrouped.evening.map((parsed) => (
                    <TimeSlotButton
                      key={parsed.slot.appointmentId}
                      bg={
                        selectedSlot?.slot.appointmentId === parsed.slot.appointmentId
                          ? optumBlue
                          : slotBg
                      }
                      borderColor={
                        selectedSlot?.slot.appointmentId === parsed.slot.appointmentId
                          ? accent
                          : timeSlotBorder
                      }
                      onPress={() => setSelectedSlot(parsed)}
                    >
                      <TimeSlotLabel
                        color={
                          selectedSlot?.slot.appointmentId === parsed.slot.appointmentId
                            ? textOnAccent
                            : optumBlue
                        }
                      >
                        {parsed.time12h}
                      </TimeSlotLabel>
                    </TimeSlotButton>
                  ))}
                </TimeSlotGrid>
              </>
            )}
          </div>

          {/* Confirm / cancel actions */}
          <ModalActionRow>
            <CancelButton borderColor={cancelButtonBorder} onPress={onClose}>
              <CancelButtonLabel color={optumBlue}>Cancel</CancelButtonLabel>
            </CancelButton>
            <SaveButton
              bg={selectedSlot ? optumBlue : textDeactivated}
              onPress={handleSave}
              disabled={!selectedSlot}
            >
              <SaveButtonContent>
                <SaveButtonLabel color={textOnAccent}>Save</SaveButtonLabel>
                <SaveButtonIcon color={textOnAccent}>›</SaveButtonIcon>
              </SaveButtonContent>
            </SaveButton>
          </ModalActionRow>
        </ModalSheet>
      </ModalOverlay>
    </ModalDialog>
  );
};
